import React, { useState, useRef, useEffect } from "react";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getExams } from "../../services/ExamService";
import { toast } from "react-toastify";
import RightList from "./RightList";
import { getUserInfoById } from "../../services/UserService";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/solid";

const ExamCard = ({ exam, onSwipeRight, onSwipeLeft }) => {
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const containerRef = useRef(null);

  const handleStart = (clientX) => {
    if (exam.buttonColor === "gray") return; // Không cho kéo nếu đang cập nhật
    setIsDragging(true);
  };

  const handleMove = (clientX) => {
    if (!isDragging || !containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const maxSlide = containerRect.width - 60; // 60px là width của slider button

    let newPosition = clientX - containerRect.left - 30; // 30px là offset
    newPosition = Math.max(0, Math.min(newPosition, maxSlide));

    setSliderPosition(newPosition);
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const maxSlide = containerRect.width - 60;
    const threshold = maxSlide * 0.8; // 80% của chiều dài

    if (sliderPosition >= threshold) {
      // Đã kéo đủ xa -> trigger action
      if (exam.isDone) {
        onSwipeLeft(exam.id);
      } else {
        onSwipeRight(exam.id);
      }
      setSliderPosition(maxSlide); // Hoàn thành animation
      setTimeout(() => setSliderPosition(0), 300);
    } else {
      // Chưa đủ -> reset về vị trí ban đầu
      setSliderPosition(0);
    }
  };

  // Touch events
  const handleTouchStart = (e) => {
    e.preventDefault();
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  // Add global mouse move/up listeners when dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, sliderPosition]);

  const isDisabled = exam.buttonColor === "gray" || !exam.isActive;
  const isInactive = !exam.isActive;

  return (
    <div className={`relative bg-[#f6d2df] rounded-md p-6 shadow-xl`}>
      {/* Access Badge - Premium/Miễn phí */}
      <div className="absolute top-4 right-4">
        <span
          className={`text-xs px-2 py-1 rounded-full font-bold ${
            exam.access === "Premium"
              ? "bg-[#db3b6d] text-white"
              : "bg-white text-[#4b4b4b] border border-[#4b4b4b]"
          }`}
        >
          {exam.access === "Premium" ? "PREMIUM" : "MIỄN PHÍ"}
        </span>
      </div>

      {/* Lock Icon */}
      <div
        className={`absolute top-4 left-4 rounded-[0.5rem] py-2 px-4 ${
          exam.isActive
            ? "bg-[#db3b6d]"
            : "bg-[#f6d2df] border border-[#4b4b4b]"
        }`}
      >
        {exam.isActive ? (
          <LockOpenIcon
            className={`w-10 h-10 ${
              isInactive ? "text-[#4b4b4b]" : "text-white"
            }`}
          />
        ) : (
          <LockClosedIcon
            className={`w-10 h-10 ${
              isInactive ? "text-[#4b4b4b]" : "text-[#db3b6d]"
            }`}
          />
        )}
      </div>

      {/* Title */}
      <h3
        className={`font-extrabold text-2xl mt-2 mb-6 ml-20 ${
          isInactive ? "text-[#4b4b4b]" : "text-[#db3b6d]"
        }`}
      >
        {exam.title}
      </h3>

      {/* Info */}
      <ul className="space-y-2 mb-4 list-[circle] list-inside">
        <li className="text-[#4b4b4b] text-sm">
          Số lượt thi: <span className="font-semibold">{exam.attempts}</span>
        </li>
        <li className="text-[#4b4b4b] text-sm">
          Trạng thái: <span className="font-semibold">{exam.status}</span>
        </li>
      </ul>

      {/* Slide to unlock button or inactive button */}
      {isInactive ? (
        <div className="flex justify-end">
          <div className="relative w-[60%] h-12 rounded-md bg-[#4b4b4b] flex items-center justify-center">
            <span className="font-medium text-sm text-white">
              ĐANG CẬP NHẬT
            </span>
          </div>
        </div>
      ) : (
        <div
          ref={containerRef}
          className={`relative w-full h-12 rounded-md overflow-hidden ${
            isDisabled ? "bg-[#4b4b4b]" : "bg-white border-2 border-[#db3b6d]"
          }`}
        >
          {/* Background text */}
          <div
            className={`absolute inset-0 flex items-center justify-center pointer-events-none ${
              isDisabled ? "ml-0" : "ml-20"
            }`}
          >
            <span
              className={`font-medium text-sm ${
                isDisabled ? "text-white" : "text-[#db3b6d]"
              }`}
              style={{
                opacity: isDisabled ? 1 : Math.max(0, 1 - sliderPosition / 50),
              }}
            >
              {exam.buttonText}
            </span>
          </div>

          {/* Slider button */}
          {!isDisabled && (
            <div
              ref={sliderRef}
              className="absolute top-0 left-0 h-full w-20 bg-[#db3b6d] rounded-r-[5px] flex items-center justify-center cursor-grab active:cursor-grabbing select-none shadow-md"
              style={{
                transform: `translateX(${sliderPosition}px)`,
                transition: isDragging ? "none" : "transform 0.3s ease",
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
            >
              <ArrowRightIcon className="w-6 h-6 text-white" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PracticeRoomPage = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const examsPerPage = 6;

  // Fetch exams from API
  useEffect(() => {
    fetchExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchExams = async () => {
    try {
      setLoading(true);
      const response = await getExams({
        page: currentPage,
        limit: examsPerPage,
      });

      if (response?.data) {
        // Map exams from API to ExamCard format
        const mappedExams = response.data.map((exam) => ({
          id: exam._id,
          title: exam.title || "Đề thi",
          status: exam.isDone ? "Đã làm bài" : "Chưa làm bài",
          attempts: exam.attemptNumber || 0,
          buttonText: exam.isDone
            ? "KÉO ĐỂ XEM KẾT QUẢ"
            : "KÉO SANG PHẢI ĐỂ VÀO THI",
          buttonColor: exam.isActive === false ? "gray" : "pink",
          isDone: exam.isDone || false,
          isActive: exam.isActive !== false, // Default to true if not specified
          access: exam.access || "Miễn phí", // Default to "Miễn phí" if not specified
        }));
        setExams(mappedExams);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching exams:", error);
      toast.error("Không thể tải danh sách đề thi");
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    }
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleSwipeRight = async (examId) => {
    const exam = exams.find((exam) => exam.id === examId);
    if (exam.access === "Premium") {
      const user = await getUserInfoById();
      if (!user.data?.premium) {
        toast.error("Bạn cần nâng cấp tài khoản để xem đề thi");
        return;
      }
    }
    navigate(`/exam/${examId}`);
  };

  const handleSwipeLeft = (examId) => {
    navigate(`/exam/${examId}`);
  };

  return (
    <div className="min-h-screen bg-[#f9dfe8] pb-16">
      {/* Section title bar */}
      <div className="max-w-[100rem] mx-auto px-4 flex items-center">
        <div className="mt-4 inline-block bg-[#db3b6d] text-white px-6 py-3 rounded-r-md rounded-l-none font-bold shadow ml-[-100vw] pl-[100vw]">
          PHÒNG LUYỆN ĐỀ
        </div>
        <div className="flex items-center gap-2 mt-4">
          <ChevronRight className="text-[#4b4b4b] ml-[5px] w-8 h-8" />
          <span className="text-[#4b4b4b] text-lg">ĐỀ THI TSA</span>
        </div>
      </div>

      <section className="max-w-[88rem] mx-auto px-4 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8">
          {/* Left column: cards + pagination */}
          <div>
            {/* Exam Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {loading ? (
                // Loading skeleton
                [...Array(6)].map((_, idx) => (
                  <div
                    key={idx}
                    className="relative bg-[#f1c0cf] rounded-md p-6 shadow-[0_8px_20px_rgba(219,59,109,0.15)] animate-pulse"
                  >
                    <div className="w-12 h-12 bg-gray-300 rounded mb-4" />
                    <div className="h-6 bg-gray-300 rounded w-3/4 mb-4 ml-20" />
                    <div className="space-y-2 mb-4">
                      <div className="h-4 bg-gray-300 rounded w-1/2" />
                      <div className="h-4 bg-gray-300 rounded w-1/2" />
                    </div>
                    <div className="h-12 bg-gray-300 rounded" />
                  </div>
                ))
              ) : exams.length > 0 ? (
                exams.map((exam) => (
                  <ExamCard
                    key={exam.id}
                    exam={exam}
                    onSwipeRight={handleSwipeRight}
                    onSwipeLeft={handleSwipeLeft}
                  />
                ))
              ) : (
                <div className="col-span-full text-center py-12 text-gray-500">
                  Chưa có đề thi nào
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-full bg-[#f1c0cf] hover:bg-[#db3b6d] disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center justify-center shadow transition-colors"
                  aria-label="Trang trước"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => handlePageClick(i + 1)}
                      className={`w-8 h-8 rounded-full transition-colors flex items-center justify-center text-sm font-medium ${
                        currentPage === i + 1
                          ? "bg-[#db3b6d] text-white"
                          : "bg-white border-2 border-[#4b4b4b] text-[#4b4b4b] hover:bg-[#f1c0cf]"
                      }`}
                      aria-label={`Trang ${i + 1}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-full bg-[#f1c0cf] hover:bg-[#db3b6d] disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center justify-center shadow transition-colors"
                  aria-label="Trang sau"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Right list on large screens */}
          <div className="hidden lg:block justify-self-end">
            <RightList />
          </div>
        </div>

        {/* Right list on small screens (below pagination) */}
        <div className="mt-6 flex justify-center lg:hidden">
          <RightList />
        </div>
      </section>
    </div>
  );
};

export default PracticeRoomPage;
